'use client';

import type { ReactNode } from 'react';

import type { BaseComponentProps } from '../../base-component.types';
import { ResourceBar } from '../../resource';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../tooltip';
import type { DifficultyLevel } from '../Question.types';
import { DifficultyBadge } from './DifficultyBadge';

import styles from './InfoBar.module.scss';

type InfoBarProps = BaseComponentProps & {
  /** The icon group at the leading edge. */
  options?: ReactNode;
  difficulty?: `${DifficultyLevel}`;
  /**
   * Shown on hovering the difficulty badge. Without it the badge is plain text, since a
   * tooltip that says nothing more than the badge already does is only in the way.
   */
  difficultyDetails?: ReactNode;
  /**
   * Drops the padding around the details, for details that bring a pane of their own —
   * an assessment's scoreboard.
   */
  difficultyDetailsFlush?: boolean;
  children?: ReactNode;
};

function badge(
  difficulty: `${DifficultyLevel}`,
  details?: ReactNode,
  detailsFlush?: boolean,
) {
  const rendered = <DifficultyBadge difficulty={difficulty} />;

  if (!details) {
    return rendered;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild={true}>{rendered}</TooltipTrigger>
      <TooltipContent className={styles['details']} flush={detailsFlush}>
        {details}
      </TooltipContent>
    </Tooltip>
  );
}

/** The bar carrying what a question or an assessment is like: how hard it is, and
 * whatever else is worth knowing about it. */
function InfoBar({
  style,
  className,
  options,
  difficulty,
  difficultyDetails,
  difficultyDetailsFlush,
  children,
  testId,
}: InfoBarProps) {
  return (
    <ResourceBar
      style={style}
      className={className}
      edge="bottom"
      testId={testId}
      start={options}
      center={
        difficulty &&
        badge(difficulty, difficultyDetails, difficultyDetailsFlush)
      }
      end={children}
    />
  );
}

export { InfoBar };
